import { useLocation } from "react-router-dom";

// Each value is the trailing URL path segment of a child route registered
// under `/sessions/:sessionId` in `SessionsRoutes`. The string values are
// load-bearing: they are matched against `location.pathname` to identify
// the active tab, and used as `<Link to={...}>` targets to navigate
// between tabs. Renaming a value requires updating the matching
// `<Route path>` in `SessionsRoutes`.
export enum SessionTab {
  Topics = "topics",
  Sources = "sources",
  Details = "details",
}

// The default branch fires on the transient render before `SessionsRoutes`
// commits its index/wildcard `<Navigate to={SessionTab.Topics} replace />`.
// Concrete triggers: `/sessions/:sessionId` (no trailing segment, so `pop()`
// returns the sessionId) and `/sessions/:sessionId/<unknown>`. After the
// redirect, the hook re-runs with `topics` or `sources`.
function deriveActiveTabFromUrl(path: string | undefined): SessionTab {
  switch (path) {
    case SessionTab.Topics:
      return SessionTab.Topics;
    case SessionTab.Sources:
      return SessionTab.Sources;
    case SessionTab.Details:
      return SessionTab.Details;
    default:
      return SessionTab.Topics;
  }
}

/**
 * Returns the active session-detail tab derived from the trailing
 * segment of the current URL pathname. `useLocation` re-renders the
 * component on every pathname change, so the URL is the single source
 * of truth and the derivation runs inline on each render.
 */
export function useActiveSessionTab(): SessionTab {
  const location = useLocation();
  const path = location.pathname.split("/").pop();
  return deriveActiveTabFromUrl(path);
}

/**
 * True when ``pathname`` matches ``/sessions/:sessionId`` exactly (with
 * an optional trailing slash) — i.e. no tab segment is present. Used by
 * the error-state fallback to re-issue the same default-tab redirect that
 * ``SessionsRoutes`` would normally emit through its index ``<Navigate>``,
 * which doesn't run when ``SessionLayout`` unmounts on error. Encoded as
 * an exact match because ``endsWith`` would misclassify a path like
 * ``/sessions/<id>/<id>`` as bare.
 */
export function isBareSessionUrl(pathname: string, sessionId: string): boolean {
  return pathname.replace(/\/$/, "") === `/sessions/${sessionId}`;
}
